import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { LayoutDashboard, BookOpen, Users, DollarSign, FileText, ScrollText, Settings2, MessageSquare, LogOut, Shield, Handshake, Wallet, Video, Mail, Quote } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/brand/Logo";

const LINKS = [
    { to: "/admin", icon: LayoutDashboard, label: "Overview", end: true, testId: "admin-nav-overview" },
    { to: "/admin/curriculum", icon: BookOpen, label: "Curriculum", testId: "admin-nav-curriculum" },
    { to: "/admin/students", icon: Users, label: "Students", testId: "admin-nav-students" },
    { to: "/admin/pricing", icon: DollarSign, label: "Pricing", testId: "admin-nav-pricing" },
    { to: "/admin/blog", icon: FileText, label: "Blog", testId: "admin-nav-blog" },
    { to: "/admin/testimonials", icon: Quote, label: "Testimonials", testId: "admin-nav-testimonials" },
    { to: "/admin/affiliates", icon: Handshake, label: "Affiliates", testId: "admin-nav-affiliates" },
    { to: "/admin/payouts", icon: Wallet, label: "Payouts", testId: "admin-nav-payouts" },
    { to: "/admin/journal", icon: MessageSquare, label: "Journal Review", testId: "admin-nav-journal" },
    { to: "/admin/live-sessions", icon: Video, label: "Live Sessions", testId: "admin-nav-live-sessions" },
    { to: "/admin/email-log", icon: Mail, label: "Email Log", testId: "admin-nav-email-log" },
    { to: "/admin/audit-log", icon: ScrollText, label: "Audit Log", testId: "admin-nav-audit" },
    { to: "/admin/settings", icon: Settings2, label: "Settings", testId: "admin-nav-settings" },
];

export default function AdminLayout() {
    const { user, signOut } = useAuth();
    const location = useLocation();

    async function handleSignOut() {
        await signOut();
        window.location.replace("/");
    }

    return (
        <div className="min-h-screen flex" style={{ background: "var(--st-silver-50)" }} data-testid="admin-layout">
            {/* Sidebar */}
            <aside
                className="w-64 flex-shrink-0 hidden md:flex flex-col"
                style={{ background: "var(--st-navy)", color: "var(--st-white)" }}
                data-testid="admin-sidebar"
            >
                <div className="px-6 py-6" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <Link to="/" className="inline-flex" data-testid="admin-sidebar-logo">
                        <Logo linkTo={null} variant="dark" compact />
                    </Link>
                    <div className="mt-2 tracking-caps text-[9px] font-semibold" style={{ color: "#9AA6C4" }}>
                        Admin Console
                    </div>
                </div>
                <nav className="flex-1 py-4 space-y-0.5 px-3">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.end}
                            data-testid={l.testId}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                                    isActive
                                        ? "bg-white/10 text-white"
                                        : "text-[#C4CBDA] hover:bg-white/5 hover:text-white"
                                }`
                            }
                        >
                            <l.icon size={16} strokeWidth={1.6} />
                            <span>{l.label}</span>
                        </NavLink>
                    ))}
                </nav>
                <div className="p-4" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                    <div className="text-xs" style={{ color: "#9AA6C4" }}>Signed in as</div>
                    <div className="text-sm font-semibold mt-0.5 truncate" data-testid="admin-header-name">
                        {user?.name || user?.email}
                    </div>
                    <button
                        type="button"
                        onClick={handleSignOut}
                        className="mt-3 w-full text-left flex items-center gap-2 text-sm text-[#C4CBDA] hover:text-white"
                        data-testid="admin-signout-btn"
                    >
                        <LogOut size={14} /> Sign out
                    </button>
                </div>
            </aside>

            {/* Content */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Mobile top bar */}
                <header
                    className="md:hidden flex items-center justify-between px-4 h-14"
                    style={{ background: "var(--st-navy)", color: "var(--st-white)" }}
                >
                    <Logo variant="dark" compact />
                    <button type="button" onClick={handleSignOut} className="text-sm" data-testid="admin-signout-btn-mobile">
                        Sign out
                    </button>
                </header>
                <main className="flex-1 overflow-x-auto" data-testid="admin-main-content" key={location.pathname}>
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
